import { useMemo, useState } from "react";
import { Activity, Heart, Waves, Bike, Footprints } from "lucide-react";
import { cn } from "@/lib/utils";
import { CHART, TOKENS } from "../../lib/designTokens";
import { StatusBadge } from "../StatusBadge";
import { ProgressMetricCard } from "./ProgressMetricCard";
import { inputCls } from "./ProgressFilters";

const labels = {
  measured: "Measured",
  device_reported: "Device-reported",
  apex_estimated: "Endurava estimate",
};

const SPORT_VO2_LABELS = {
  cycling: "Cycling VO₂ Max",
  running: "Running VO₂ Max",
  swimming: "Swimming VO₂ Max",
};

const SPORT_ICONS = {
  cycling: Bike,
  running: Footprints,
  swimming: Waves,
};

const reasonText = {
  missing_weight: "Add a valid body weight.",
  missing_power: "A power meter is required for this cycling method.",
  estimated_power_not_allowed: "Estimated power is not used.",
  insufficient_effort: "Complete a suitable maximal effort.",
  poor_gps_quality: "GPS quality was insufficient.",
  sensor_dropout: "Power sensor coverage was insufficient.",
  unstable_effort: "The effort was too unstable for this method.",
  corrupt_samples: "The activity contains invalid power samples.",
  unreliable_indoor_speed: "Indoor speed was not trustworthy enough.",
  trail_grade_unadjusted: "Trail pace is not treated as flat-road pace.",
  swimming_estimation_not_enabled: "Swimming VO₂ Max estimation is not available from this session type.",
};

const sourceType = (row) => row?.source_type || "apex_estimated";

const confidenceVariant = (label) => {
  const lower = String(label || "").toLowerCase();
  if (lower.includes("high")) return "completed";
  if (lower.includes("medium")) return "planned";
  if (lower.includes("low")) return "load";
  return "neutral";
};

const trendVariant = (status) => {
  const lower = String(status || "").toLowerCase();
  if (lower.includes("improv")) return "completed";
  if (lower.includes("declin")) return "load";
  if (lower.includes("stable")) return "planned";
  return "neutral";
};

const SPARSE_VO2_HISTORY_THRESHOLD = 3;

const HistoryPlot = ({ rows }) => {
  const points = useMemo(() => {
    const valid = (rows || [])
      .filter((row) => Number.isFinite(Number(row.value_ml_kg_min)) && row.estimate_date)
      .sort((a, b) => new Date(a.estimate_date) - new Date(b.estimate_date));
    if (!valid.length) return [];
    const values = valid.map((row) => Number(row.value_ml_kg_min));
    const min = Math.min(...values) - 2;
    const max = Math.max(...values) + 2;
    return valid.map((row, index) => ({
      ...row,
      x: valid.length === 1 ? 50 : 5 + (index / (valid.length - 1)) * 90,
      y: 90 - ((Number(row.value_ml_kg_min) - min) / Math.max(1, max - min)) * 80,
    }));
  }, [rows]);
  if (!points.length) return null;

  const isSparse = points.length <= SPARSE_VO2_HISTORY_THRESHOLD;
  const countLabel = `${points.length} recorded estimate${points.length === 1 ? "" : "s"}`;

  return (
    <div className="mt-5 app-card-elevated p-4" data-testid="aerobic-history-chart">
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
        <div className="card-label">VO₂ Max history · ml/kg/min</div>
        {isSparse ? (
          <p className="text-xs text-dark-text-muted" data-testid="aerobic-history-sparse-label">
            {countLabel}
          </p>
        ) : null}
      </div>
      <svg
        viewBox="0 0 100 100"
        className={cn(
          "w-full rounded-md border border-dark-border/70 bg-dark-background/50",
          isSparse ? "h-28 sm:h-32" : "h-44 sm:h-48"
        )}
        role="img"
        aria-label={`VO2 Max estimate history, ${countLabel}`}
      >
        {points.map((point, index) => (
          <g key={`${point.estimate_date}-${index}`}>
            {point.uncertainty_low != null && point.uncertainty_high != null ? (
              <line
                x1={point.x}
                x2={point.x}
                y1={Math.max(4, point.y - 7)}
                y2={Math.min(96, point.y + 7)}
                stroke={CHART.tick}
                strokeWidth="1"
              />
            ) : null}
            <circle
              cx={point.x}
              cy={point.y}
              r="2.5"
              fill={
                sourceType(point) === "measured"
                  ? TOKENS.analytics.green
                  : sourceType(point) === "device_reported"
                    ? TOKENS.analytics.amber
                    : TOKENS.analytics.blue
              }
            >
              <title>{`${Number(point.value_ml_kg_min).toFixed(1)} ml/kg/min on ${new Date(point.estimate_date).toLocaleDateString()}`}</title>
            </circle>
          </g>
        ))}
      </svg>
    </div>
  );
};

export const AerobicFitnessSection = ({ data, loading, error, onCreateMeasurement }) => {
  const [sport, setSport] = useState("cycling");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    value: "",
    date: "",
    method: "Laboratory test",
    laboratory_name: "",
    notes: "",
  });
  const selected = data?.sports?.find((row) => row.sport === sport);
  const current = selected?.current;
  const SportIcon = SPORT_ICONS[sport] || Activity;
  const vo2Label = SPORT_VO2_LABELS[sport] || `${sport} VO₂ Max`;

  const submit = async (event) => {
    event.preventDefault();
    await onCreateMeasurement?.({ ...form, value: Number(form.value), sport });
    setShowForm(false);
    setForm({ value: "", date: "", method: "Laboratory test", laboratory_name: "", notes: "" });
  };

  return (
    <div data-testid="aerobic-fitness-content">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <select
          value={sport}
          onChange={(event) => setSport(event.target.value)}
          className={inputCls}
          aria-label="Aerobic fitness sport"
        >
          <option value="cycling">Cycling</option>
          <option value="running">Running</option>
          <option value="swimming">Swimming</option>
        </select>
        <button
          type="button"
          onClick={() => setShowForm((value) => !value)}
          className="btn-secondary-app text-[10px] tracking-widest uppercase"
        >
          Add measured result
        </button>
      </div>

      {showForm ? (
        <form
          onSubmit={submit}
          className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4 app-card-elevated p-4"
          data-testid="measured-vo2-form"
        >
          <input
            required
            type="number"
            min="15"
            max="90"
            step="0.1"
            aria-label="VO2 Max value"
            placeholder="VO₂ Max (ml/kg/min)"
            value={form.value}
            onChange={(e) => setForm({ ...form, value: e.target.value })}
            className={inputCls}
          />
          <input
            required
            type="date"
            aria-label="Measurement date"
            value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })}
            className={inputCls}
          />
          <input
            required
            aria-label="Measurement method"
            value={form.method}
            onChange={(e) => setForm({ ...form, method: e.target.value })}
            className={inputCls}
          />
          <input
            aria-label="Laboratory name"
            placeholder="Laboratory/provider (optional)"
            value={form.laboratory_name}
            onChange={(e) => setForm({ ...form, laboratory_name: e.target.value })}
            className={inputCls}
          />
          <button
            type="submit"
            className="sm:col-span-2 btn-primary-app text-xs uppercase tracking-widest"
          >
            Save measured result
          </button>
        </form>
      ) : null}

      {loading ? (
        <p className="mt-5 text-sm text-dark-text-secondary">Loading aerobic fitness…</p>
      ) : null}
      {error ? (
        <p className="mt-5 text-sm text-analytics-red">Aerobic fitness data is temporarily unavailable.</p>
      ) : null}
      {!loading && !error && !current ? (
        <div className="mt-5 app-card-elevated p-5 border-dashed" data-testid="aerobic-empty-state">
          <div className="text-dark-text-primary font-medium">No eligible {sport} estimate yet</div>
          <p className="text-xs text-dark-text-secondary mt-2">
            {reasonText[selected?.exclusion_reasons?.[0]] || "More eligible activities are needed for a stable estimate."}
          </p>
        </div>
      ) : null}
      {current ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mt-5">
            <ProgressMetricCard
              icon={SportIcon}
              label={vo2Label}
              value={Number(current.value_ml_kg_min).toFixed(1)}
              unit="ml/kg/min"
              prominent
              accent={TOKENS.analytics.green}
              soft="rgba(49, 185, 120, 0.14)"
              subtitle={[
                labels[sourceType(current)],
                current.estimate_date ? new Date(current.estimate_date).toLocaleDateString() : null,
                current.method ? current.method.replaceAll("_", " ") : null,
              ].filter(Boolean).join(" · ")}
            />
            <ProgressMetricCard
              icon={Heart}
              label="Confidence"
              value={null}
              status={(
                <StatusBadge variant={confidenceVariant(current.confidence_label)}>
                  {current.confidence_label || "Unavailable"}
                </StatusBadge>
              )}
              subtitle={
                current.uncertainty_low != null
                  ? `Range ${Number(current.uncertainty_low).toFixed(1)}–${Number(current.uncertainty_high).toFixed(1)} ml/kg/min`
                  : sourceType(current) === "apex_estimated"
                    ? "Uncertainty not quantified"
                    : "Direct supplied value"
              }
              accent={TOKENS.analytics.amber}
              soft="rgba(217, 154, 43, 0.14)"
            />
            <ProgressMetricCard
              icon={Activity}
              label="Aerobic trend"
              value={null}
              status={(
                <StatusBadge variant={trendVariant(selected?.trend?.status)}>
                  {selected?.trend?.status?.replace("_", " ") || "Insufficient data"}
                </StatusBadge>
              )}
              subtitle="Trend is separate from VO₂ Max."
              accent={TOKENS.analytics.blue}
              soft="rgba(47, 142, 234, 0.14)"
            />
          </div>
          <HistoryPlot rows={selected?.history} />
        </>
      ) : null}
      <p className="text-[11px] text-dark-text-secondary mt-4 leading-relaxed">
        {data?.disclaimer || "Endurava estimates are not laboratory measurements or medical advice."}
      </p>
    </div>
  );
};
